import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import * as userProfileService from "../services/userProfileService";

// ==================== QUERY HOOKS ====================

/**
 * Hook to fetch user details
 */
export const useUserDetails = (userId) => {
  return useQuery({
    queryKey: ["userDetails", userId],
    queryFn: () => userProfileService.fetchUserDetails(userId),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });
};

/**
 * Hook to fetch subscription details
 */
export const useSubscriptionDetails = (userId) => {
  return useQuery({
    queryKey: ["subscriptionDetails", userId],
    queryFn: () => userProfileService.fetchSubscriptionDetails(userId),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
    retry: 2,
    refetchOnMount: "always"
  });
};

/**
 * Hook to fetch attorney details
 */
export const useAttorneyDetails = (userId) => {
  return useQuery({
    queryKey: ["attorneyDetails", userId],
    queryFn: () => userProfileService.fetchAttorneyDetails(userId),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
    retry: 2,
  });
};

/**
 * Hook to fetch plan features
 */
export const usePlanFeatures = (planId) => {
  return useQuery({
    queryKey: ["planFeatures", planId],
    queryFn: () => userProfileService.fetchFeatures(planId),
    enabled: !!planId,
    staleTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
    refetchOnMount: "always"
  });
};



/**
 * Hook to save updation requirement
 */
export const useSaveUpdationRequirement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: userProfileService.saveUpdationRequirement,
    onSuccess: (_, variables) => {
      // Invalidate user details so the updation_requirement refreshes
      queryClient.invalidateQueries({
        queryKey: ["userDetails", variables.userId],
      });
      toast.success("Updation requirement saved successfully!");
    },
    onError: (error) => {
      toast.error(error.message || "Failed to save updation requirement");
    },
  });
};



/**
 * Hook to fetch subscription history
 */
export const useSubscriptionHistory = (userId) => {
  return useQuery({
    queryKey: ["subscriptionHistory", userId],
    queryFn: () => userProfileService.fetchSubscriptionHistory(userId),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
    retry: 2,
  });
};


/**
 * Hook to fetch API calls with pagination
 */
export const useApiCalls = (userId, page = 1, limit = 10) => {
  return useQuery({
    queryKey: ["apiCalls", userId, page, limit],
    queryFn: () => userProfileService.fetchApiCalls({ userId, page, limit }),
    enabled: !!userId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    keepPreviousData: true, // Keep previous data while fetching new page
    retry: 2,
  });
};

/**
 * Hook to fetch wallet transactions with pagination
 */
export const useWalletTransactions = (userId, page = 1, limit = 10) => {
  return useQuery({
    queryKey: ["walletTransactions", userId, page, limit],
    queryFn: () => userProfileService.fetchWalletTransactions({ userId, page, limit }),
    enabled: !!userId,
    staleTime: 2 * 60 * 1000,
    keepPreviousData: true,
    retry: 2,
  });
};

// ==================== MUTATION HOOKS ====================

/**
 * Hook to update user details
 */
export const useUpdateUserDetails = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: userProfileService.updateUserDetails,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["subscriptionDetails", variables.userId] });
      toast.success("User details updated successfully!");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || error.message || "Failed to update user details");
    },
  });
};

/**
 * Hook to update subscription plan
 */
export const useUpdateSubscription = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: userProfileService.updateSubscriptionPlan,
    onSuccess: (data, variables) => {
      // Don't refresh when upgrade is held for approval — the plan hasn't changed yet
      if (data?.pending_approval) return;
      queryClient.invalidateQueries({ queryKey: ["subscriptionDetails", variables.userId] });
      queryClient.invalidateQueries({ queryKey: ["userDetails", variables.userId] });
    },
    onError: (error) => {
      toast.error(error.message || "Failed to update subscription");
    },
  });
};

/**
 * Hook to update plan features
 */
export const useUpdatePlanFeatures = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: userProfileService.updatePlanFeatures,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["planFeatures"] });
      queryClient.invalidateQueries({ queryKey: ["subscriptionDetails", variables.userId] });
      toast.success("Plan features updated successfully!");
    },
    onError: (error) => {
      toast.error(error.message || "Failed to update plan features");
    },
  });
};

/**
 * Hook to enable / disable a single feature for a user.
 * Payload: { userId, featureId, action: "enable" | "disable" }
 *
 * Invalidating subscriptionDetails re-reads get-subscription-details, which is the
 * source of truth for the Features tab — the response's own feature list is also
 * returned to the caller for an immediate optimistic render.
 */
export const useUpdateUserFeature = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: userProfileService.updateUserFeature,
    onSuccess: (data, variables) => {
      // Write the API's own feature list straight into the cache so the table
      // updates immediately, without waiting for the refetch to land.
      if (data?.features != null) {
        queryClient.setQueriesData(
          { queryKey: ["subscriptionDetails"] },
          (old) =>
            old?.subscription_plan
              ? {
                  ...old,
                  subscription_plan: {
                    ...old.subscription_plan,
                    features: data.features,
                  },
                }
              : old,
        );
      }
      // Invalidate by prefix only: the query key holds the id from useParams
      // (a string) while mutations pass a numeric id, so keying on the id here
      // would never match and the tab would keep showing stale data.
      queryClient.invalidateQueries({ queryKey: ["subscriptionDetails"] });
      toast.success(
        variables.action === "enable" ? "Feature enabled" : "Feature disabled",
      );
    },
    onError: (error) => {
      toast.error(error.message || "Failed to update feature");
    },
  });
};

/**
 * Hook to add new attorney numbers via /add-attorney-numbers
 * Payload: { userId, attorneyNumbers: [{ number, tm_count }] }
 */
export const useAddAttorneyNumbers = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: userProfileService.addAttorneyNumbers,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["attorneyDetails", variables.userId] });
      queryClient.invalidateQueries({ queryKey: ["subscriptionDetails", variables.userId] });
      toast.success("Attorney numbers added successfully!");
    },
    onError: (error) => {
      toast.error(error.message || "Failed to add attorney numbers");
    },
  });
};

/**
 * Remove one attorney number.
 *
 * The response already contains the remaining list and the new total, so the
 * cached profile is patched with those rather than invalidated — a refetch here
 * would ask the server for something it has just told us, and the table would
 * flicker through a loading state on the way.
 */
export const useDeleteAttorneyNumber = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: userProfileService.deleteAttorneyNumber,
    onSuccess: (res, variables) => {
      queryClient.setQueryData(["subscriptionDetails", variables.userId], (old) => {
        if (!old) return old;
        const logs = Array.isArray(old.subscription_logs) ? [...old.subscription_logs] : [];
        if (logs[0] && res?.total_tm_count !== undefined) {
          logs[0] = { ...logs[0], total_tm_count: res.total_tm_count };
        }
        return {
          ...old,
          attorney_numbers: Array.isArray(res?.data) ? res.data : old.attorney_numbers,
          subscription_logs: logs,
        };
      });
      toast.success(res?.message || "Attorney number deleted.");
    },
    onError: (error) => {
      toast.error(error.message || "Failed to delete the attorney number");
    },
  });
};

/**
 * Hook to update attorney numbers
 */
export const useUpdateAttorneyNumbers = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: userProfileService.updateAttorneyNumbers,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["attorneyDetails", variables.userId] });
      queryClient.invalidateQueries({ queryKey: ["subscriptionDetails", variables.userId] });
      toast.success("Attorney numbers updated successfully!");
    },
    onError: (error) => {
      toast.error(error.message || "Failed to update attorney numbers");
    },
  });
};

/**
 * Hook to update user status
 */
export const useUpdateUserStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: userProfileService.updateUserStatus,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["subscriptionDetails", variables.userId] });
      toast.success("User status updated successfully!");
    },
    onError: (error) => {
      toast.error(error.message || "Failed to update user status");
    },
  });
};
